// Map of lowercased color/tee name tokens → hex color values.
// Covers the vast majority of real-world golf tee naming conventions.
const COLOR_MAP: Record<string, string> = {
  // Pure colors
  white:       '#FFFFFF',
  black:       '#1A1A1A',
  red:         '#CC0000',
  blue:        '#1565C0',
  green:       '#2E7D32',
  yellow:      '#F9CA00',
  orange:      '#E65100',
  purple:      '#6A0DAD',
  pink:        '#E91E8C',
  grey:        '#9E9E9E',
  gray:        '#9E9E9E',

  // Common golf tee names that map to a specific color
  gold:        '#DAA520',
  silver:      '#A8A8A8',
  bronze:      '#CD7F32',
  copper:      '#B87333',
  platinum:    '#E0E0E0',
  champagne:   '#F7E7CE',

  // Named shades
  navy:        '#0A1F5C',
  maroon:      '#800000',
  crimson:     '#DC143C',
  scarlet:     '#FF2400',
  teal:        '#007070',
  sage:        '#7B8C5A',
  tan:         '#D2B48C',
  ruby:        '#9B111E',
  emerald:     '#046307',
  cobalt:      '#0047AB',
  indigo:      '#3F51B5',
  violet:      '#7F00FF',
  lavender:    '#9575CD',
  coral:       '#FF6B6B',
  rose:        '#E91E8C',
  ivory:       '#FFFFF0',
  cream:       '#FFFDD0',
  sand:        '#C2A96E',
  dune:        '#C2A96E',
  clay:        '#B6694B',
  rust:        '#B7410E',
  wine:        '#722F37',
  burgundy:    '#800020',
  slate:       '#607D8B',
  charcoal:    '#36454F',
  onyx:        '#353839',

  // Golf-specific conventions
  championship: '#1A1A1A',
  forward:      '#CC0000',
  ladies:       '#CC0000',
  junior:       '#E65100',
  senior:       '#F9CA00',
};

// Separators used in combo tee names, e.g. "Red/White" or "Blue-Gold"
const COMBO_SEPARATORS = /[/\-–]/;

function lookupColor(token: string): string | undefined {
  const normalized = token.trim().toLowerCase();
  if (COLOR_MAP[normalized]) return COLOR_MAP[normalized];

  // Try substring match for names like "Championship Blue" → blue
  for (const [key, hex] of Object.entries(COLOR_MAP)) {
    if (normalized.includes(key)) return hex;
  }
  return undefined;
}

export interface TeeColorGuess {
  color?: string;
  color2?: string;
  combo?: true;
}

/**
 * Infers tee color(s) from a tee set name.
 * Returns only the fields it's confident about; unrecognized names return {}.
 */
export function guessTeeColor(name: string): TeeColorGuess {
  if (!name) return {};

  const parts = name.split(COMBO_SEPARATORS);

  if (parts.length >= 2) {
    const c1 = lookupColor(parts[0]);
    const c2 = lookupColor(parts[1]);
    if (c1 && c2) return { color: c1, color2: c2, combo: true };
    if (c1)       return { color: c1 };
    if (c2)       return { color: c2 };
  }

  const color = lookupColor(name);
  return color ? { color } : {};
}
